import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import IconButton from "../components/IconButton";

/*
 * Player control primitives shared by the class-page card and the expanded
 * sheet ("docked bar" language, player redesign 2026-10): the seek/volume
 * slider, the mute control with its focus-or-hover volume slider, and the
 * ⋯ overflow menu. All borderless, 44px, light gray on navy.
 */

export function Scrubber({ value, max, onSeek, ariaLabel = "Seek", trackHeight = 5, thumb = 14, width }) {
  const pct = max > 0 ? Math.min(100, (value / max) * 100) : 0;
  return (
    <div
      role="slider"
      aria-label={ariaLabel}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(pct)}
      tabIndex={0}
      onClick={(e) => {
        const rect = e.currentTarget.getBoundingClientRect();
        const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
        onSeek(ratio * max);
      }}
      onKeyDown={(e) => {
        if (e.key === "ArrowLeft") onSeek(Math.max(0, value - Math.max(5, max * 0.05)));
        if (e.key === "ArrowRight") onSeek(Math.min(max, value + Math.max(5, max * 0.05)));
      }}
      className="relative flex items-center cursor-pointer rounded-xl focus-visible:outline-none focus-visible:shadow-ic-focus-dark"
      style={{ height: 24, width, flex: width ? undefined : 1 }}
    >
      <div className="w-full rounded-[3px] bg-white/20 overflow-hidden" style={{ height: trackHeight }}>
        <div className="h-full bg-ic-sky" style={{ width: `${pct}%` }} />
      </div>
      <div
        className="absolute rounded-full bg-white shadow-ic-raised-dark pointer-events-none"
        style={{ left: `${pct}%`, top: "50%", transform: "translate(-50%, -50%)", width: thumb, height: thumb }}
      />
    </div>
  );
}

export const MuteGlyph = ({ muted }) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M11 5L6 9H2v6h4l5 4V5z" fill="currentColor" stroke="none" />
    {muted ? (
      <>
        <path d="M16 9l6 6" />
        <path d="M22 9l-6 6" />
      </>
    ) : (
      <>
        <path d="M15.5 8.5a5 5 0 0 1 0 7" />
        <path d="M18.5 5.5a9 9 0 0 1 0 13" />
      </>
    )}
  </svg>
);

const MoreGlyph = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <circle cx="5" cy="12" r="2" />
    <circle cx="12" cy="12" r="2" />
    <circle cx="19" cy="12" r="2" />
  </svg>
);

/** True on devices with a real pointer; touch devices use hardware volume, so no slider there. */
function useFinePointer() {
  const query = "(hover: hover) and (pointer: fine)";
  const [fine, setFine] = useState(() => typeof window !== "undefined" && window.matchMedia?.(query).matches);
  useEffect(() => {
    const mq = window.matchMedia?.(query);
    if (!mq) return undefined;
    const onChange = () => setFine(mq.matches);
    mq.addEventListener?.("change", onChange);
    return () => mq.removeEventListener?.("change", onChange);
  }, []);
  return fine;
}

/*
 * Mute toggle; on pointer devices a volume slider slides out beside it on
 * hover *or* keyboard focus (focus-within), so it's never hover-only — and
 * the mute button itself is always there.
 */
export function VolumeControl({ muted, volume, onMute, onVolume, slider = true }) {
  const fine = useFinePointer();
  return (
    <div className="group flex items-center">
      <IconButton variant="bareDark" size={44} dark label={muted ? "Unmute" : "Mute"} onClick={onMute}>
        <MuteGlyph muted={muted} />
      </IconButton>
      {fine && slider && (
        <div className="w-0 overflow-hidden opacity-0 transition-all duration-150 ease-out group-hover:w-[76px] group-hover:opacity-100 group-focus-within:w-[76px] group-focus-within:opacity-100">
          <div className="px-2">
            <Scrubber ariaLabel="Volume" value={muted ? 0 : volume} max={1} onSeek={onVolume} trackHeight={4} thumb={12} width={60} />
          </div>
        </div>
      )}
    </div>
  );
}

const menuItemClasses =
  "w-full min-h-[44px] px-3 flex items-center gap-3 rounded-lg bg-transparent border-0 text-left font-sans text-[15px] font-semibold leading-5 text-ic-offwhite no-underline cursor-pointer hover:bg-white/10 hover:text-white hover:no-underline focus-visible:outline-none focus-visible:bg-white/10";

/*
 * The ⋯ menu — items come from the caller (card: watch/listen, download,
 * and like/bookmark when compact; sheet: class page, download). Opened from
 * the keyboard, focus moves to the first item, ↑/↓ move between items, and
 * Escape closes and returns focus to ⋯. Opened by tap/click, focus is left
 * alone. An outside click closes.
 */
export function MoreMenu({ items }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  // Move focus into the menu (and back to ⋯ on close) only for keyboard use;
  // doing it after a tap leaves a focus ring on ⋯ on touch devices.
  const viaKeyboard = useRef(false);

  const focusItem = (dir) => {
    const els = Array.from(menuRef.current?.querySelectorAll('[role^="menuitem"]:not([aria-disabled="true"])') || []);
    if (!els.length) return;
    const idx = els.indexOf(document.activeElement);
    const next = dir === "first" ? 0 : (idx + dir + els.length) % els.length;
    els[next].focus();
  };

  useEffect(() => {
    if (!open) return undefined;
    if (viaKeyboard.current) focusItem("first");
    const onDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("pointerdown", onDown);
    return () => document.removeEventListener("pointerdown", onDown);
  }, [open]);

  const close = (fromKeyboard = viaKeyboard.current) => {
    setOpen(false);
    if (fromKeyboard) triggerRef.current?.focus();
  };

  return (
    <div ref={rootRef} className="relative">
      <IconButton
        ref={triggerRef}
        variant="bareDark"
        size={44}
        dark
        label="More options"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={(e) => {
          viaKeyboard.current = e.detail === 0; // detail 0 = Enter/Space activation
          setOpen((o) => !o);
        }}
      >
        <MoreGlyph />
      </IconButton>
      {open && (
        <div
          ref={menuRef}
          role="menu"
          aria-label="More options"
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              // Close only the menu — not a surrounding sheet that also listens for Escape
              e.preventDefault();
              e.stopPropagation();
              close(true);
            } else if (e.key === "ArrowDown") {
              e.preventDefault();
              focusItem(1);
            } else if (e.key === "ArrowUp") {
              e.preventDefault();
              focusItem(-1);
            } else if (e.key === "Tab") {
              setOpen(false);
            }
          }}
          className="absolute right-0 bottom-full mb-2 z-20 min-w-[248px] p-1.5 bg-ic-navyCard border border-white/10 rounded-xl shadow-ic-raised-dark flex flex-col"
        >
          {items.map((item) =>
            item.href ? (
              <a
                key={item.key}
                role="menuitem"
                href={item.href}
                download
                target="_blank"
                rel="noreferrer"
                onClick={() => {
                  item.onSelect?.();
                  setOpen(false);
                }}
                className={menuItemClasses}
              >
                {item.icon}
                {item.label}
              </a>
            ) : (
              <button
                key={item.key}
                type="button"
                role={item.checked === undefined ? "menuitem" : "menuitemcheckbox"}
                aria-checked={item.checked}
                aria-disabled={item.disabled || undefined}
                onClick={(e) => {
                  if (item.disabled) return;
                  item.onSelect();
                  close(e.detail === 0);
                }}
                className={`${menuItemClasses} ${item.disabled ? "opacity-50 cursor-not-allowed hover:bg-transparent" : ""}`}
              >
                {item.icon}
                <span className="flex-1">{item.label}</span>
                {item.note && <span className="text-[12px] font-normal text-ic-lightGray">{item.note}</span>}
              </button>
            )
          )}
        </div>
      )}
    </div>
  );
}

export const BookGlyph = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
    <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
  </svg>
);

/**
 * An element's border-box size, measured before first paint (so layouts that
 * branch on it never flash) and kept current with a ResizeObserver.
 */
export function useElementSize(ref) {
  const [size, setSize] = useState({ width: 0, height: 0 });
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const measure = () => {
      const r = el.getBoundingClientRect();
      setSize((prev) => (prev.width === r.width && prev.height === r.height ? prev : { width: r.width, height: r.height }));
    };
    measure();
    if (typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref]);
  return size;
}
